import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classNames from 'classnames';
import theme, { Theme } from 'helpers/theme';
import Icon, { IconNames } from '../../../sharedComponents/core/Icon/Icon';
import ProfileIcon from 'sharedComponents/core/ProfileIcon';
import { useActionMenuDispatch } from 'components/ActionMenu/ActionMenuProvider';
import { Logout } from 'helpers/logout';
import { useRouter } from 'found';
import {
  faRightFromBracket,
  faUserPen
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEllipsis,
  faEllipsisH,
  faEllipsisVertical
} from '@fortawesome/free-solid-svg-icons';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    zIndex: 10,
    height: '100%',
    flexDirection: 'column',
    borderRight: '1px solid #EDEDED',
    background: 'white',
    position: 'relative',
    justifyContent: 'space-between'
  },
  menu: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    backgroundColor: theme.colors.primaryWhite
  },
  tab: {
    width: '100%',
    gap: '15px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '6px 0',
    transition: 'all 0.1s',
    '&:hover div': {
      color: theme.colors.primaryBlack
    },
    '&:hover $menuIconGradient': {
      background: '#f7f9ff'
    },
    '&:hover $menuIcon': {
      background: '#e4e6ec'
    },
    '&:hover': {
      background: '#f7f9ff'
    }
  },
  selected: {
    backgroundColor: '#eaf0fb !important',
    transition: 'background-color 0.3s linear, opacity 0.3s linear'
  },
  fold: {
    height: '40px',
    width: '5px',
    borderRadius: '0 9px 9px 0',
    backgroundColor: theme.colors.primaryBlue,
    opacity: 1
  },
  noFold: {
    height: '40px',
    width: '5px',
    opacity: 0
  },
  body: {
    padding: '30px 30px',
    backgroundColor: theme.colors.backgroundGrey,
    boxShadow: theme.shadows.body,
    flexGrow: 1
  },
  menuItemText: {
    color: theme.colors.primaryGrey,
    fontWeight: 700,
    fontSize: 13,
    transition: 'all 0.1s'
  },
  menuItemTextSelected: {
    extend: 'menuItemText',
    color: theme.colors.primaryBlack
  },
  menuIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#F6F6F7',
    borderRadius: 8,
    width: 32,
    height: 32,
    transition: 'all 0.1s'
  },
  menuIconGradient: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'white',
    width: 29,
    height: 29,
    borderRadius: 6.5
  },
  menuIconSelected: {
    extend: 'menuIcon',
    background:
      'linear-gradient(180deg, rgb(21 194 43), rgb(11 87 254)) !important',
    boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.05)'
  },
  menuHeader: {
    fontWeight: 700,
    fontSize: 11,
    color: theme.colors.primaryGrey,
    marginLeft: 21,
    marginBottom: 11,
    marginTop: 30
  },
  userInfo: {
    display: 'grid',
    gridTemplateColumns: '50px 1fr 36px',
    bottom: 0,
    borderTop: '1px solid #EDEDED',
    width: '100%',
    padding: '17px 17px',
    alignItems: 'center'
  },
  userImage: {
    width: 37,
    height: 37,
    borderRadius: 7,
    background: 'blue'
  },
  userDescription: {},
  userName: {
    fontSize: 14,
    fontWeight: 700,
    color: theme.colors.primaryBlack
  },
  userJob: {
    fontSize: 12,
    fontWeight: 400,
    color: '#6a728f'
  },
  moreButton: {
    color: '#6A728F',
    fontSize: 18,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    height: 36,
    '&:hover': {
      color: theme.colors.primaryBlack,
      background: '#f7f7f8'
    }
  },
  companyLogoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 21
  },
  companyLogo: {
    maxWidth: 170,
    maxHeight: 55
  }
}));

export interface Tab {
  id: number;
  icon?: any;
  iconComponent?: any;
  size?: number;
  name: string;
}

type Props = {
  tabs: Array<Tab>;
  companyTabs: Array<Tab>;
  selected: Tab;
  onClick: (tab: Tab) => void;
  className?: string;
  manager?: {
    firstName: string;
    lastName: string;
    profileImageUrl: string | null;
    company: {
      logoURL: string | null;
      name: string;
    };
  };
  onEditDetails?: () => void;
};

function SideMenu({
  tabs,
  companyTabs,
  selected,
  onClick,
  className,
  manager,
  onEditDetails
}: Props) {
  const cssTheme = useTheme();
  const classes = useStyles({ theme: cssTheme });

  const dispatch = useActionMenuDispatch();

  const { router } = useRouter();

  const makeTab = (tab: Tab) => (
    <a
      tabIndex={0}
      key={tab.id}
      className={classNames(
        classes.tab,
        selected && selected.id === tab.id && classes.selected
      )}
      onClick={(evt) => {
        evt.preventDefault();
        onClick(tab);
      }}
    >
      <div
        className={classNames(
          selected && selected.id === tab.id ? classes.fold : classes.noFold
        )}
      />
      <div
        className={
          selected && selected.id === tab.id
            ? classes.menuIconSelected
            : classes.menuIcon
        }
      >
        <div className={classes.menuIconGradient}>
          {tab.iconComponent && (
            <div
              style={{
                color:
                  selected && selected.id === tab.id
                    ? theme?.colors?.primaryBlue
                    : theme?.colors?.primaryGrey,
                fontSize: 13
              }}
            >
              {tab.iconComponent}
            </div>
          )}
          {tab.icon && !tab.iconComponent && (
            <tab.icon
              color={
                selected && selected.id === tab.id
                  ? theme?.colors?.primaryBlue
                  : theme?.colors?.primaryGrey
              }
            />
          )}
        </div>
      </div>
      <div
        className={
          selected && selected.id === tab.id
            ? classes.menuItemTextSelected
            : classes.menuItemText
        }
      >
        {tab.name}
      </div>
    </a>
  );

  return (
    <div className={classNames(classes.root, className)}>
      <nav>
        <div className={classes.menuHeader}>MENU</div>
        <div className={classNames(classes.menu)}>
          {tabs && tabs.map((tab) => makeTab(tab))}
        </div>
        {companyTabs && companyTabs.length > 0 && (
          <>
            <div className={classes.menuHeader}>COMPANY</div>
            <div className={classNames(classes.menu)}>
              {companyTabs && companyTabs.map((tab) => makeTab(tab))}
            </div>
          </>
        )}
      </nav>

      <div>
        <div className={classes.companyLogoContainer}>
          {manager?.company?.logoURL && (
            <img
              src={manager?.company?.logoURL}
              className={classes.companyLogo}
            />
          )}
        </div>
        <div className={classes.userInfo}>
          <ProfileIcon
            url={manager?.profileImageUrl ?? undefined}
            name={`${manager?.firstName} ${manager?.lastName}`}
            size={35}
          />
          <div className={classes.userDescription}>
            <div className={classes.userName}>
              {manager?.firstName} {manager?.lastName}
            </div>
            <div className={classes.userJob}>{manager?.company.name}</div>
          </div>
          <div
            onClick={(evt) => {
              dispatch({
                type: 'show',
                options: [
                  {
                    name: 'Edit details',
                    onClick: onEditDetails,
                    icon: faUserPen
                  },
                  {
                    name: 'Log out',
                    onClick: () => {
                      Logout(
                        () => {},
                        () => {}
                      );
                      window.location.href = '/login';
                    },
                    icon: faRightFromBracket
                  }
                ],
                position: {
                  x: evt.clientX,
                  y: evt.clientY,
                  placement: 'top-right'
                }
              });
            }}
            className={classes.moreButton}
          >
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default SideMenu;
